import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import RevolutCheckout from '@revolut/checkout';
import { CreditCard, Loader2, Trash2, AlertCircle, CheckCircle, X, Plus, Star } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Account Settings > Payment Methods section (Phase D — multiple cards).
 *
 * Lets the user:
 *   • View ALL saved cards (brand + last4) with a "Default" badge
 *   • Set any card as the default (used for auto top-ups & one-tap checkout)
 *   • Add a new card (via a $1 Revolut setup order) up to a 5-card cap
 *   • Delete an individual card
 */
export const PaymentMethodSection = () => {
  const { user } = useApp();

  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [maxCards, setMaxCards] = useState(5);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [actionBusy, setActionBusy] = useState('');

  // Card setup flow state
  const [showCardSetup, setShowCardSetup] = useState(false);
  const [setupToken, setSetupToken] = useState(null);
  const [setupOrderId, setSetupOrderId] = useState(null);
  const [setupSaving, setSetupSaving] = useState(false);
  const [setupCardholder, setSetupCardholder] = useState('');
  const setupCardFieldRef = useRef(null);
  const setupCardInstanceRef = useRef(null);

  // Delete confirmation (per-card)
  const [confirmDeleteId, setConfirmDeleteId] = useState('');

  const load = async () => {
    if (!user?.email) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/payment-methods/${encodeURIComponent(user.email)}`);
      setCards(res.data?.cards || []);
      if (res.data?.max) setMaxCards(res.data.max);
    } catch (e) {
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const atLimit = cards.length >= maxCards;

  const handleOpenSetup = async () => {
    setError('');
    setSuccessMsg('');
    if (atLimit) {
      setError(`You can save up to ${maxCards} cards. Delete one to add another.`);
      return;
    }
    setSetupSaving(true);
    try {
      const resp = await axios.post(`${API}/revolut/setup-card-order`, {
        user_email: user.email,
      });
      if (resp.data?.success) {
        setSetupToken(resp.data.token);
        setSetupOrderId(resp.data.order_id);
        setShowCardSetup(true);
      } else {
        setError('Could not start card setup. Please try again.');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start card setup.');
    } finally {
      setSetupSaving(false);
    }
  };

  // Mount Revolut card field when setup modal opens
  useEffect(() => {
    if (!showCardSetup || !setupToken || !setupCardFieldRef.current) return;
    let mounted = true;
    if (setupCardInstanceRef.current?.destroy) {
      setupCardInstanceRef.current.destroy();
      setupCardInstanceRef.current = null;
    }
    (async () => {
      try {
        const instance = await RevolutCheckout(setupToken);
        if (!mounted) return;
        const cardField = instance.createCardField({
          target: setupCardFieldRef.current,
          onSuccess: async () => {
            try {
              const r = await axios.post(`${API}/revolut/setup-card-complete`, {
                revolut_order_id: setupOrderId,
                user_email: user.email,
              });
              if (r.data?.success) {
                setShowCardSetup(false);
                setSetupToken(null);
                setSetupOrderId(null);
                setSuccessMsg('Card saved. You\'re all set.');
                await load();
              } else {
                setError(r.data?.message || 'Card could not be saved.');
              }
            } catch (e) {
              setError(e.response?.data?.detail || 'Card setup failed.');
            } finally {
              setSetupSaving(false);
            }
          },
          onError: (err) => {
            setError(err?.message || 'Card setup failed.');
            setSetupSaving(false);
          },
          styles: {
            default: { color: '#1f2937', fontSize: '15px', '::placeholder': { color: '#9ca3af' } },
          },
        });
        setupCardInstanceRef.current = cardField;
      } catch (err) {
        console.error('Setup card field init error:', err);
        setError('Could not load card form. Please try again.');
      }
    })();
    return () => {
      mounted = false;
      if (setupCardInstanceRef.current?.destroy) setupCardInstanceRef.current.destroy();
      setupCardInstanceRef.current = null;
    };
  }, [showCardSetup, setupToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitCardSetup = (e) => {
    e.preventDefault();
    if (!setupCardInstanceRef.current || setupSaving) return;
    setSetupSaving(true);
    setError('');
    setupCardInstanceRef.current.submit({
      name: setupCardholder.trim() || user?.firstName || 'Card Holder',
      email: user?.email || '',
      savePaymentMethodFor: 'merchant',
      billingAddress: { countryCode: 'AU', postcode: '2000' },
    });
    setTimeout(() => setSetupSaving(false), 15000);
  };

  const handleSetDefault = async (pmId) => {
    setError('');
    setSuccessMsg('');
    setActionBusy(`default-${pmId}`);
    try {
      await axios.post(`${API}/payment-methods/${encodeURIComponent(user.email)}/default`, {
        payment_method_id: pmId,
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not set default card.');
    } finally {
      setActionBusy('');
    }
  };

  const handleDelete = async (pmId) => {
    setError('');
    setSuccessMsg('');
    setActionBusy(`delete-${pmId}`);
    try {
      const res = await axios.delete(`${API}/payment-methods/${encodeURIComponent(user.email)}/${encodeURIComponent(pmId)}`);
      if (res.data?.success) {
        setConfirmDeleteId('');
        const disabled = res.data.auto_topups_disabled || 0;
        setSuccessMsg(
          disabled > 0
            ? `Card deleted. Auto top-up was also disabled on ${disabled} eSIM${disabled === 1 ? '' : 's'}.`
            : 'Card deleted.'
        );
        await load();
      } else {
        setError('Could not delete card.');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not delete card.');
    } finally {
      setActionBusy('');
    }
  };

  return (
    <div
      className="bg-white dark:bg-neutral-900 rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100 dark:border-neutral-800 mb-6"
      data-testid="payment-method-section"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gray-100 dark:bg-neutral-800 rounded-full flex items-center justify-center">
          <CreditCard size={20} className="text-gray-600 dark:text-neutral-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100">Saved Payment Methods</h2>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 dark:text-neutral-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : cards.length > 0 ? (
        <div className="space-y-3" data-testid="saved-cards-list">
          {cards.map((card) => {
            const pmId = card.revolut_payment_method_id;
            const confirming = confirmDeleteId === pmId;
            return (
              <div
                key={pmId}
                className="p-4 rounded-xl border border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-800/50"
                data-testid={`saved-card-row-${pmId}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-8 rounded bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 flex items-center justify-center text-xs font-bold uppercase text-gray-700 dark:text-neutral-300">
                      {card.card_brand || 'CARD'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 dark:text-neutral-100">
                          •••• {card.card_last4 || '••••'}
                        </p>
                        {card.is_default && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
                            data-testid={`default-badge-${pmId}`}
                          >
                            <Star size={10} className="fill-current" /> Default
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-neutral-500">
                        Used for auto top-ups &amp; one-tap checkout
                      </p>
                    </div>
                  </div>
                  {!confirming && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {!card.is_default && (
                        <button
                          type="button"
                          onClick={() => handleSetDefault(pmId)}
                          disabled={!!actionBusy}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-700 text-xs font-semibold text-gray-800 dark:text-neutral-200 hover:bg-white dark:hover:bg-neutral-800 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                          data-testid={`set-default-btn-${pmId}`}
                        >
                          {actionBusy === `default-${pmId}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Star size={13} />}
                          Set default
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(pmId)}
                        disabled={!!actionBusy}
                        className="p-2 rounded-lg border border-red-200 dark:border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-50"
                        data-testid={`delete-card-btn-${pmId}`}
                        aria-label="Delete card"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </div>

                {confirming && (
                  <div
                    className="mt-3 p-3 rounded-xl border border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 space-y-3"
                    data-testid={`delete-confirm-box-${pmId}`}
                  >
                    <p className="text-sm text-red-800 dark:text-red-300">
                      Delete this card? {card.is_default && cards.length > 1 ? 'Another card will become your default. ' : ''}
                      {cards.length === 1 ? 'Any active auto top-ups will be disabled.' : ''}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleDelete(pmId)}
                        disabled={!!actionBusy}
                        className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid={`confirm-delete-card-btn-${pmId}`}
                      >
                        {actionBusy === `delete-${pmId}` ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes, delete'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId('')}
                        disabled={!!actionBusy}
                        className="px-4 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 text-sm font-semibold text-gray-700 dark:text-neutral-300 hover:bg-white dark:hover:bg-neutral-800 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={handleOpenSetup}
            disabled={setupSaving || atLimit}
            className="w-full mt-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-gray-300 dark:border-neutral-700 text-gray-800 dark:text-neutral-200 font-semibold hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
            data-testid="add-card-btn"
          >
            {setupSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus size={16} />}
            {atLimit ? `Card limit reached (${maxCards})` : 'Add another card'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-neutral-400">
            You don&apos;t have a card saved. Add one to enable automatic top-ups and one-tap checkout.
            A small $1 AUD charge will be captured and added to your account as credit.
          </p>
          <button
            type="button"
            onClick={handleOpenSetup}
            disabled={setupSaving}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors disabled:opacity-50 dark:border dark:border-white"
            data-testid="add-first-card-btn"
          >
            {setupSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard size={18} />}
            Add a card
          </button>
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-center gap-2 text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-500/10 rounded-xl" data-testid="payment-method-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="mt-4 flex items-center gap-2 text-green-600 dark:text-emerald-400 text-sm p-3 bg-green-50 dark:bg-emerald-500/10 rounded-xl" data-testid="payment-method-success">
          <CheckCircle size={18} />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Card setup modal */}
      {showCardSetup && (
        <div
          className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="card-setup-modal"
        >
          <div className="bg-white dark:bg-neutral-900 rounded-2xl max-w-md w-full p-6 border border-gray-100 dark:border-white/10 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100">
                Save a card
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowCardSetup(false);
                  setSetupToken(null);
                  setSetupOrderId(null);
                }}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-800"
                data-testid="close-card-setup-btn"
                aria-label="Close"
              >
                <X size={20} className="text-gray-600 dark:text-neutral-400" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-neutral-500 mb-4">
              A $1 AUD charge will be applied to verify the card and added to your account
              as credit. The card is saved for future auto top-ups and one-tap checkout.
            </p>
            <form onSubmit={handleSubmitCardSetup} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-neutral-400 mb-1">
                  Cardholder name
                </label>
                <input
                  type="text"
                  value={setupCardholder}
                  onChange={(e) => setSetupCardholder(e.target.value)}
                  placeholder={user?.firstName || 'Card holder'}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 text-sm focus:outline-none focus:border-black dark:focus:border-white"
                  data-testid="setup-cardholder-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-neutral-400 mb-1">
                  Card details
                </label>
                <div
                  ref={setupCardFieldRef}
                  className="min-h-[44px] px-3 py-2 rounded-lg border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950"
                  data-testid="setup-card-field"
                />
              </div>
              <button
                type="submit"
                disabled={setupSaving}
                className="w-full px-4 py-3 rounded-xl bg-black text-white font-bold hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 dark:border dark:border-white"
                data-testid="submit-card-setup-btn"
              >
                {setupSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save card ($1 credit)'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodSection;
